/**
 * BetSlip — floating component
 *
 * variant="drawer"  — mobile fixed bottom drawer (slides up, z-50)
 * variant="sidebar" — desktop right sidebar (no toggle button)
 *
 * Features:
 *  - Leg list with stake inputs
 *  - Single / Accumulator toggle (2+ legs)
 *  - Total stake + potential payout display
 *  - Place Bet action with loading / success toast / error inline
 *  - Balance guard (amber warning + Deposit link)
 *  - Empty state
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useBetSlip } from '../contexts/BetSlipContext';
import { useAuth } from '../contexts/AuthContext';
import { placeBet, type BetLegRequest } from '../services/api';
import { formatZAR } from '../utils/currency';

// ── Toast ────────────────────────────────────────────────────────────────────

function Toast({ message, onDone }: { message: string; onDone: () => void }) {
  // Auto-dismiss after 3 s
  useState(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  });
  return (
    <div
      role="status"
      className="mx-4 mb-3 rounded-button bg-success/10 px-4 py-3 text-center text-sm font-medium text-success"
    >
      {message}
    </div>
  );
}

// ── Single Leg Row ────────────────────────────────────────────────────────────

interface LegRowProps {
  leg: {
    selection_id: string;
    event_name: string;
    market_name: string;
    selection_name: string;
    odds: number;
    stake_cents: number;
  };
  betType: 'single' | 'accumulator';
  accumulatorStakeRands: string;
  onAccumulatorChange: (val: string) => void;
  onRemove: () => void;
  onStakeChange: (cents: number) => void;
}

function LegRow({
  leg,
  betType,
  accumulatorStakeRands,
  onAccumulatorChange,
  onRemove,
  onStakeChange,
}: LegRowProps) {
  const stakeRands =
    betType === 'accumulator'
      ? accumulatorStakeRands
      : leg.stake_cents > 0
        ? String(leg.stake_cents / 100)
        : '';

  const handleChange = (val: string) => {
    if (betType === 'accumulator') {
      onAccumulatorChange(val);
    } else {
      const n = parseFloat(val);
      onStakeChange(isNaN(n) ? 0 : Math.round(n * 100));
    }
  };

  return (
    <li className="flex flex-col gap-1 border-b border-border py-3 last:border-0">
      {/* Header row */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-xs text-text-muted">{leg.event_name}</p>
          <p className="text-sm font-medium text-text-primary leading-tight">
            {leg.selection_name}
          </p>
          <p className="text-xs text-text-secondary">{leg.market_name}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="rounded-pill bg-primary/10 px-2 py-0.5 text-sm font-bold text-primary">
            {leg.odds.toFixed(2)}
          </span>
          <button
            type="button"
            aria-label={`Remove ${leg.selection_name}`}
            onClick={onRemove}
            className="text-text-muted hover:text-error transition-colors text-lg leading-none"
          >
            ×
          </button>
        </div>
      </div>
      {/* Stake input — singles only; accumulator input rendered separately */}
      {betType === 'single' && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-muted">R</span>
          <input
            type="number"
            min="1"
            step="1"
            placeholder="Stake"
            value={stakeRands}
            onChange={(e) => handleChange(e.target.value)}
            className="min-h-[36px] w-full rounded-button border border-border bg-surface px-3 py-1 text-sm text-text-primary outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
          />
        </div>
      )}
    </li>
  );
}

// ── Slip Inner Content ────────────────────────────────────────────────────────

interface SlipContentProps {
  onClose?: () => void;
  showCloseButton: boolean;
}

function SlipContent({ onClose, showCloseButton }: SlipContentProps) {
  const {
    legs,
    betType,
    setBetType,
    totalStake,
    potentialPayout,
    removeLeg,
    updateStake,
    clearSlip,
  } = useBetSlip();

  const { user } = useAuth();

  const [submitting, setSubmitting] = useState(false);
  const [successToast, setSuccessToast] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Accumulator stake stored as ZAR string
  const [accStakeRands, setAccStakeRands] = useState('');

  const legCount = legs.length;
  const balanceCents = user?.balance_cents ?? 0;
  const accStakeCents = accStakeRands ? Math.round(parseFloat(accStakeRands) * 100) : 0;
  const displayTotalStake = betType === 'accumulator' ? accStakeCents : totalStake;
  const combinedOdds = legs.reduce((acc, l) => acc * l.odds, 1);
  const displayPayout =
    betType === 'accumulator'
      ? Math.round(accStakeCents * combinedOdds)
      : potentialPayout;

  const insufficientBalance =
    displayTotalStake > 0 && balanceCents < displayTotalStake;

  const handlePlaceBet = async () => {
    setSubmitError(null);
    if (legCount === 0) return;

    const betLegs: BetLegRequest[] = legs.map((l) => ({
      selection_id: l.selection_id,
      market_id: l.market_id,
      stake_cents: betType === 'accumulator' ? accStakeCents : l.stake_cents,
    }));

    const hasZeroStake = betLegs.some((bl) => bl.stake_cents <= 0);
    if (hasZeroStake) {
      setSubmitError('Enter a stake for each selection.');
      return;
    }

    if (insufficientBalance) {
      setSubmitError('Insufficient balance. Please deposit more funds.');
      return;
    }

    setSubmitting(true);
    try {
      await placeBet(betLegs, betType);
      setSuccessToast('Bet placed! ✓');
      clearSlip();
      setAccStakeRands('');
      if (onClose) onClose();
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 402 || status === 400) {
        setSubmitError(detail ?? 'Insufficient balance — Deposit more.');
      } else {
        setSubmitError(detail ?? 'Failed to place bet. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <aside
      className="flex h-full flex-col bg-white"
      aria-label="Bet Slip"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="font-heading text-base font-bold text-text-primary">
          Bet Slip {legCount > 0 && `(${legCount})`}
        </span>
        <div className="flex items-center gap-2">
          {legCount > 0 && (
            <button
              type="button"
              onClick={clearSlip}
              className="text-xs text-text-muted hover:text-error transition-colors"
            >
              Clear all
            </button>
          )}
          {showCloseButton && onClose && (
            <button
              type="button"
              onClick={onClose}
              className="ml-1 text-text-muted hover:text-text-primary text-xl leading-none"
              aria-label="Close bet slip"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Body (scrollable) */}
      <div className="flex-1 overflow-y-auto px-4">
        {successToast && (
          <div className="mt-3">
            <Toast message={successToast} onDone={() => setSuccessToast(null)} />
          </div>
        )}

        {legCount === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <span className="mb-3 text-4xl">🎯</span>
            <p className="text-sm text-text-muted">
              Add selections to build your bet slip
            </p>
          </div>
        ) : (
          <>
            {/* Bet type toggle (2+ legs) */}
            {legCount >= 2 && (
              <div className="mt-3 flex rounded-button border border-border overflow-hidden">
                {(['single', 'accumulator'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setBetType(t)}
                    className={[
                      'flex-1 py-2 text-sm font-medium transition-colors capitalize',
                      betType === t
                        ? 'bg-primary text-white'
                        : 'bg-white text-text-secondary hover:bg-surface-2',
                    ].join(' ')}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}

            {/* Legs */}
            <ul className="mt-2">
              {legs.map((leg, idx) => (
                <LegRow
                  key={leg.selection_id}
                  leg={leg}
                  betType={betType}
                  accumulatorStakeRands={idx === 0 ? accStakeRands : ''}
                  onAccumulatorChange={(val) => setAccStakeRands(val)}
                  onRemove={() => removeLeg(leg.selection_id)}
                  onStakeChange={(cents) => updateStake(leg.selection_id, cents)}
                />
              ))}
            </ul>

            {/* Accumulator stake input */}
            {betType === 'accumulator' && (
              <div className="mt-3 rounded-button border border-border p-3">
                <p className="mb-1 text-xs font-medium text-text-muted">
                  Combined odds: {combinedOdds.toFixed(2)}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-text-muted">R</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    placeholder="Stake (ZAR)"
                    value={accStakeRands}
                    onChange={(e) => setAccStakeRands(e.target.value)}
                    className="min-h-[36px] w-full rounded-button border border-border bg-surface px-3 py-1 text-sm text-text-primary outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      {legCount > 0 && (
        <div className="border-t border-border px-4 pb-4 pt-3">
          <div className="mb-2 flex justify-between text-sm">
            <span className="text-text-muted">Total stake</span>
            <span className="font-semibold text-text-primary">
              {formatZAR(displayTotalStake)}
            </span>
          </div>
          <div className="mb-3 flex justify-between text-sm">
            <span className="text-text-muted">Potential payout</span>
            <span className="font-bold text-success">{formatZAR(displayPayout)}</span>
          </div>

          {insufficientBalance && (
            <div className="mb-3 rounded-button bg-warning/10 px-3 py-2 text-sm text-warning">
              Insufficient balance.{' '}
              <Link to="/wallet" className="font-semibold underline">
                Deposit
              </Link>
            </div>
          )}

          {submitError && (
            <p className="mb-3 text-xs text-error" role="alert">
              {submitError}
            </p>
          )}

          <button
            type="button"
            onClick={handlePlaceBet}
            disabled={submitting || insufficientBalance}
            className="min-h-[44px] w-full cursor-pointer rounded-button bg-primary py-3 font-bold text-white shadow-btn transition-colors hover:bg-primary-dark disabled:opacity-60"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="shimmer inline-block h-4 w-4 rounded-full" />
                Placing…
              </span>
            ) : (
              'Place Bet'
            )}
          </button>

          {/* Per-bet rules footnote */}
          <p className="mt-2 text-center text-[11px] leading-snug text-text-muted">
            Voided selections settle at odds 1.00 (refunded in an acca). Max
            payout limits apply.{' '}
            <Link to="/betting-rules" className="underline hover:text-primary">
              Betting rules
            </Link>
          </p>
        </div>
      )}
    </aside>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

interface BetSlipProps {
  /**
   * "drawer"  — mobile floating drawer (toggle button + slide-up panel)
   * "sidebar" — desktop always-visible sidebar panel
   *
   * If omitted, falls back to the legacy combined behaviour (drawer + desktop sidebar).
   */
  variant?: 'drawer' | 'sidebar';
}

export default function BetSlip({ variant }: BetSlipProps) {
  const { legs } = useBetSlip();
  const [open, setOpen] = useState(false);

  const legCount = legs.length;

  // ── Sidebar variant — always visible ─────────────────────────────────────────
  if (variant === 'sidebar') {
    return (
      <div className="rounded-card border border-border shadow-card overflow-hidden h-fit">
        <SlipContent showCloseButton={false} />
      </div>
    );
  }

  // ── Drawer variant — mobile toggle ────────────────────────────────────────────
  if (variant === 'drawer') {
    return (
      <>
        {/* Toggle button */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-pill bg-primary px-4 py-3 text-sm font-bold text-white shadow-btn"
          aria-label={open ? 'Close bet slip' : 'Open bet slip'}
        >
          Bet Slip
          {legCount > 0 && (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-cta text-xs font-bold text-white">
              {legCount}
            </span>
          )}
        </button>

        {/* Backdrop */}
        {open && (
          <div
            className="fixed inset-0 z-40 bg-black/30"
            onClick={() => setOpen(false)}
            aria-hidden
          />
        )}

        {/* Drawer panel */}
        <div
          className={[
            'fixed bottom-0 left-0 right-0 z-50 max-h-[80vh] rounded-t-2xl bg-white shadow-2xl transition-transform duration-300',
            open ? 'translate-y-0' : 'translate-y-full',
          ].join(' ')}
        >
          <SlipContent
            showCloseButton
            onClose={() => setOpen(false)}
          />
        </div>
      </>
    );
  }

  // ── Legacy fallback: combined mobile drawer + desktop right sidebar ────────────
  return (
    <>
      {/* Mobile toggle */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-pill bg-primary px-4 py-3 text-sm font-bold text-white shadow-btn md:hidden"
        aria-label={open ? 'Close bet slip' : 'Open bet slip'}
      >
        Bet Slip
        {legCount > 0 && (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-cta text-xs font-bold text-white">
            {legCount}
          </span>
        )}
      </button>

      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/30 md:hidden"
          onClick={() => setOpen(false)}
          aria-hidden
        />
      )}

      {/* Mobile drawer */}
      <div
        className={[
          'fixed bottom-0 left-0 right-0 z-50 max-h-[80vh] rounded-t-2xl bg-white shadow-2xl transition-transform duration-300 md:hidden',
          open ? 'translate-y-0' : 'translate-y-full',
        ].join(' ')}
      >
        <SlipContent showCloseButton onClose={() => setOpen(false)} />
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:fixed md:bottom-0 md:right-0 md:top-16 md:z-30 md:flex md:w-80 md:flex-col md:border-l md:border-border md:bg-white md:shadow-xl">
        <SlipContent showCloseButton={false} />
      </div>
    </>
  );
}
